import React, { useState, useEffect } from 'react';
import { useTranslation } from '@hooks/useTranslation';
import { useNotification } from '@hooks/useNotification';
import { useDateTimeHelpers } from './hooks/useDateTimeHelpers';

// Sub-components
import QuickDateSelector from './components/QuickDateSelector';
import CustomDateTimePicker from './components/CustomDateTimePicker';
import ResourceInfo from './components/ResourceInfo';
import FormActions from './components/FormActions';
import ErrorDisplay from './components/ErrorDisplay';
import DynamicBorrowingFields from './components/DynamicBorrowingFields';

import './BorrowingForm.scss';

const BorrowingForm = ({
    data,
    borrowing,
    resource = null,
    onSubmit,
    onCancel,
    isEditing = false,
    loading: externalLoading = false,
    mode = 'edit', // 'borrow', 'return', 'edit'
    targetUserId = null // For borrowing on behalf of someone
}) => {
    const { t } = useTranslation();
    const notify = useNotification();
    const {
        currentLanguage,
        gregorian,
        getDateLocale,
        getDateFormat,
        createDateObject
    } = useDateTimeHelpers();

    // Support both `data` (from common components) and `borrowing` (legacy)
    const modelData = data || borrowing;

    const [formData, setFormData] = useState({
        expected_return_at: '',
        returned_at: '',
        notes: '',
        return_notes: '',
        dynamic_fields: {}
    });
    const [expectedReturnDate, setExpectedReturnDate] = useState(null);
    const [returnedDate, setReturnedDate] = useState(null);
    const [internalLoading, setInternalLoading] = useState(false);
    const [error, setError] = useState('');

    const loading = externalLoading || internalLoading;

    useEffect(() => {
        if (modelData) {
            // Normalize dynamic_fields: extract values if they come in the enriched {value, definition} format
            let normalizedDynamicFields = {};
            if (modelData.dynamic_fields) {
                Object.entries(modelData.dynamic_fields).forEach(([key, fieldData]) => {
                    // Recursively unwrap enriched format {value, definition}
                    let val = fieldData;
                    while (val && typeof val === 'object' && 'value' in val && 'definition' in val) {
                        val = val.value;
                    }
                    normalizedDynamicFields[key] = val;
                });
            }

            // Use reserved_until as fallback if expected_return_at is not set
            const expectedReturnValue = modelData.expected_return_at || modelData.reserved_until || '';

            setFormData({
                expected_return_at: expectedReturnValue,
                notes: modelData.notes || '',
                return_notes: modelData.return_notes || '',
                dynamic_fields: normalizedDynamicFields
            });

            // Set DatePicker values
            if (expectedReturnValue) {
                setExpectedReturnDate(createDateObject(expectedReturnValue));
            }
            if (modelData.returned_at) {
                setReturnedDate(createDateObject(modelData.returned_at));
            }
        } else {
            setFormData({
                expected_return_at: '',
                notes: '',
                return_notes: '',
                dynamic_fields: {}
            });
            setExpectedReturnDate(null);
            setReturnedDate(null);
        }
        setError('');
    }, [modelData, createDateObject]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleQuickDateSelect = (quickDate) => {
        const dateObject = createDateObject(quickDate.toISOString());

        setExpectedReturnDate(dateObject);
        setFormData(prev => ({
            ...prev,
            expected_return_at: quickDate.toISOString()
        }));
    };

    const handleExpectedReturnDateChange = (date) => {
        setExpectedReturnDate(date);
        setFormData(prev => ({
            ...prev,
            expected_return_at: date ? date.toDate().toISOString() : ''
        }));
    };

    const handleReturnedDateChange = (date) => {
        setReturnedDate(date);
        setFormData(prev => ({
            ...prev,
            returned_at: date ? date.toDate().toISOString() : ''
        }));
    };

    const handleDynamicFieldsChange = (fields) => {
        setFormData(prev => ({
            ...prev,
            dynamic_fields: fields
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setInternalLoading(true);
        setError('');

        if (mode !== 'return') {
            if (!formData.expected_return_at) {
                setError(t('borrowing.expected_return_date_required'));
                notify.error(t('borrowing.expected_return_date_required'));
                setInternalLoading(false);
                return;
            }
        }

        try {
            const submitData = {
                ...formData,
                expected_return_at: formData.expected_return_at || null,
                returned_at: formData.returned_at || null
            };

            // Include target_user_id if borrowing on behalf of someone
            if (targetUserId) {
                submitData.target_user_id = targetUserId;
            }

            if (onSubmit) {
                await onSubmit(submitData);
            }
        } catch (err) {
            console.error('Failed to submit borrowing form:', err);
            setError(err.response?.data?.detail || t('common.error_occurred'));
        } finally {
            setInternalLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="borrowing-form">
            <ErrorDisplay error={error} />

            <ResourceInfo resource={resource} />

            {mode !== 'return' && (
                <>
                    <div className="form-group">
                        <label htmlFor="expected_return_at">
                            {t('borrowing.expected_return_date')}
                        </label>

                        <QuickDateSelector
                            onDateSelect={handleQuickDateSelect}
                            disabled={loading}
                        />

                        <CustomDateTimePicker
                            value={expectedReturnDate}
                            onChange={handleExpectedReturnDateChange}
                            format={getDateFormat()}
                            calendar={gregorian}
                            locale={getDateLocale()}
                            disabled={loading}
                            placeholder={t('borrowing.manual_date_selection', 'Manual date selection')}
                            minDate={new Date()}
                            showPreview={true}
                            currentLanguage={currentLanguage}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="notes">
                            {t('borrowing.notes')}
                        </label>
                        <textarea
                            id="notes"
                            name="notes"
                            value={formData.notes}
                            onChange={handleInputChange}
                            disabled={loading}
                            rows={3}
                            className="form-control"
                            placeholder={t('borrowing.notes_placeholder')}
                        />
                    </div>
                </>
            )}

            {/* Dynamic borrowing fields */}
            {resource && resource.category_id && (
                <DynamicBorrowingFields
                    categoryId={resource.category_id}
                    context={mode === 'return' ? 'return' : 'borrowing'}
                    existingFields={formData.dynamic_fields}
                    onChange={handleDynamicFieldsChange}
                    disabled={loading}
                />
            )}

            {mode === 'return' && (
                <>
                    <div className="form-group">
                        <label htmlFor="return_notes">
                            {t('borrowing.return_notes')}
                        </label>
                        <textarea
                            id="return_notes"
                            name="return_notes"
                            value={formData.return_notes}
                            onChange={handleInputChange}
                            disabled={loading}
                            rows={3}
                            className="form-control"
                            placeholder={t('borrowing.return_notes_placeholder')}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="returned_at">
                            {t('borrowing.returned_at')}
                        </label>
                        <CustomDateTimePicker
                            value={returnedDate}
                            onChange={handleReturnedDateChange}
                            format={getDateFormat()}
                            calendar={gregorian}
                            locale={getDateLocale()}
                            disabled={loading}
                            placeholder={t('borrowing.returned_at_placeholder', 'Select return date and time')}
                            maxDate={new Date()}
                        />
                    </div>
                </>
            )}

            <FormActions
                onCancel={onCancel}
                loading={loading}
                mode={mode}
                isEditing={isEditing}
            />
        </form>
    );
};

export default BorrowingForm;
